import React from 'react';
import { DollarSign, TrendingUp, Percent } from 'lucide-react';

/**
 * Секция для ввода финансовых данных и параметров расчета
 */
const FinancialDataSection = ({ 
  formData, 
  handleInputChange, 
  errors,
  investmentStrategies
}) => {
  return (
    <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-100 dark:border-blue-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <DollarSign className="w-5 h-5 mr-2 text-blue-500" />
        Финансовые параметры
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Заработная плата */}
        <div>
          <label htmlFor="salaryAmount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Ежемесячная зарплата
          </label>
          <div className="relative">
            <input
              type="text"
              id="salaryAmount"
              name="salaryAmount"
              value={formData.salaryAmount}
              onChange={handleInputChange}
              className={`form-input w-full pl-10 ${errors.salaryAmount ? 'input-error' : ''}`}
              placeholder="80000"
            />
            <div className="absolute inset-y-0 left-0 flex items-center pl-3">
              <span className="text-gray-500">₽</span>
            </div>
          </div>
          {errors.salaryAmount && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.salaryAmount}
            </p>
          )}
        </div>

        {/* Накопления */}
        <div>
          <label htmlFor="savingsAmount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Текущие накопления
          </label>
          <div className="relative">
            <input
              type="text"
              id="savingsAmount"
              name="savingsAmount"
              value={formData.savingsAmount}
              onChange={handleInputChange}
              className={`form-input w-full pl-10 ${errors.savingsAmount ? 'input-error' : ''}`}
              placeholder="100000"
            />
            <div className="absolute inset-y-0 left-0 flex items-center pl-3">
              <span className="text-gray-500">₽</span>
            </div>
          </div>
          {errors.savingsAmount && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.savingsAmount}
            </p>
          )}
        </div>

        {/* Ежемесячные отчисления на пенсию */}
        <div>
          <label htmlFor="monthlyContribution" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Ежемесячные отчисления
          </label>
          <div className="relative">
            <input
              type="text"
              id="monthlyContribution"
              name="monthlyContribution"
              value={formData.monthlyContribution}
              onChange={handleInputChange}
              className={`form-input w-full pl-10 ${errors.monthlyContribution ? 'input-error' : ''}`}
              placeholder="5000"
            />
            <div className="absolute inset-y-0 left-0 flex items-center pl-3">
              <span className="text-gray-500">₽</span>
            </div>
          </div>
          {errors.monthlyContribution && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.monthlyContribution}
            </p>
          )}
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Дополнительные взносы на пенсию
          </p>
        </div>

        {/* Рост зарплаты */}
        <div>
          <label htmlFor="annualIncreaseRate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <TrendingUp className="w-4 h-4 mr-1 text-blue-500" />
            Ежегодный рост зарплаты
          </label>
          <div className="relative">
            <input
              type="text"
              id="annualIncreaseRate"
              name="annualIncreaseRate"
              value={formData.annualIncreaseRate}
              onChange={handleInputChange}
              className={`form-input w-full pr-8 ${errors.annualIncreaseRate ? 'input-error' : ''}`}
              placeholder="4"
            />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
              <span className="text-gray-500">%</span>
            </div>
          </div>
          {errors.annualIncreaseRate && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.annualIncreaseRate}
            </p>
          )}
        </div>

        {/* Доходность инвестиций */}
        <div>
          <label htmlFor="investmentReturnRate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Percent className="w-4 h-4 mr-1 text-blue-500" />
            Доходность инвестиций
          </label>
          <div className="relative">
            <input
              type="text"
              id="investmentReturnRate"
              name="investmentReturnRate"
              value={formData.investmentReturnRate}
              onChange={handleInputChange}
              className={`form-input w-full pr-8 ${errors.investmentReturnRate ? 'input-error' : ''}`}
              placeholder="7"
            />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
              <span className="text-gray-500">%</span>
            </div>
          </div>
          {errors.investmentReturnRate && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.investmentReturnRate}
            </p>
          )}
        </div>

        {/* Инфляция */}
        <div>
          <label htmlFor="inflationRate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Percent className="w-4 h-4 mr-1 text-blue-500" />
            Прогнозируемая инфляция
          </label>
          <div className="relative">
            <input
              type="text"
              id="inflationRate"
              name="inflationRate"
              value={formData.inflationRate}
              onChange={handleInputChange}
              className={`form-input w-full pr-8 ${errors.inflationRate ? 'input-error' : ''}`}
              placeholder="4"
            />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
              <span className="text-gray-500">%</span>
            </div>
          </div>
          {errors.inflationRate && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.inflationRate}
            </p>
          )}
        </div>
      </div>

      {/* Тип пенсии */}
      <div className="mt-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Тип расчета пенсии
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <label className={`inline-flex items-center p-2 border rounded-lg cursor-pointer
            ${formData.pensionType === 'mandatory' 
              ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-700 text-blue-700 dark:text-blue-300' 
              : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'}`}>
            <input
              type="radio"
              name="pensionType"
              value="mandatory"
              checked={formData.pensionType === 'mandatory'}
              onChange={handleInputChange}
              className="form-radio h-4 w-4"
            />
            <span className="ml-2 text-sm font-medium">
              Только страховая
            </span>
          </label>
          <label className={`inline-flex items-center p-2 border rounded-lg cursor-pointer
            ${formData.pensionType === 'voluntary' 
              ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-700 text-blue-700 dark:text-blue-300' 
              : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'}`}>
            <input
              type="radio"
              name="pensionType"
              value="voluntary"
              checked={formData.pensionType === 'voluntary'}
              onChange={handleInputChange}
              className="form-radio h-4 w-4"
            />
            <span className="ml-2 text-sm font-medium">
              Только накопительная
            </span>
          </label>
          <label className={`inline-flex items-center p-2 border rounded-lg cursor-pointer
            ${formData.pensionType === 'both' 
              ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-700 text-blue-700 dark:text-blue-300' 
              : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'}`}>
            <input
              type="radio"
              name="pensionType"
              value="both"
              checked={formData.pensionType === 'both'}
              onChange={handleInputChange}
              className="form-radio h-4 w-4"
            />
            <span className="ml-2 text-sm font-medium">
              Смешанный тип
            </span>
          </label>
        </div>
        {errors.pensionType && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.pensionType}
          </p>
        )}
      </div>

      {/* Параметры накопительной части (если выбрана) */}
      {(formData.pensionType === 'voluntary' || formData.pensionType === 'both') && (
        <div className="mt-4">
          <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">
            Параметры накопительной пенсии
          </h4>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
            <label className={`inline-flex items-center p-2 border rounded-lg cursor-pointer
              ${formData.voluntaryPensionType === 'npf' 
                ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-700 text-blue-700 dark:text-blue-300' 
                : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'}`}>
              <input
                type="radio"
                name="voluntaryPensionType"
                value="npf"
                checked={formData.voluntaryPensionType === 'npf'}
                onChange={handleInputChange}
                className="form-radio h-4 w-4"
              />
              <span className="ml-2 text-sm font-medium">
                НПФ
              </span>
            </label>
            <label className={`inline-flex items-center p-2 border rounded-lg cursor-pointer
              ${formData.voluntaryPensionType === 'life_insurance' 
                ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-700 text-blue-700 dark:text-blue-300' 
                : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'}`}>
              <input
                type="radio"
                name="voluntaryPensionType"
                value="life_insurance"
                checked={formData.voluntaryPensionType === 'life_insurance'}
                onChange={handleInputChange}
                className="form-radio h-4 w-4"
              />
              <span className="ml-2 text-sm font-medium">
                Страхование жизни
              </span>
            </label>
            <label className={`inline-flex items-center p-2 border rounded-lg cursor-pointer
              ${formData.voluntaryPensionType === 'individual' 
                ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-700 text-blue-700 dark:text-blue-300' 
                : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'}`}>
              <input
                type="radio"
                name="voluntaryPensionType"
                value="individual"
                checked={formData.voluntaryPensionType === 'individual'}
                onChange={handleInputChange}
                className="form-radio h-4 w-4"
              />
              <span className="ml-2 text-sm font-medium">
                Личные инвестиции
              </span>
            </label>
          </div>
          
          {formData.voluntaryPensionType === 'npf' && (
            <div className="mb-3">
              <label htmlFor="npfReturnRate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Ожидаемая доходность НПФ
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="npfReturnRate"
                  name="npfReturnRate"
                  value={formData.npfReturnRate}
                  onChange={handleInputChange}
                  className={`form-input w-full pr-8 ${errors.npfReturnRate ? 'input-error' : ''}`}
                  placeholder="6"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                  <span className="text-gray-500">%</span>
                </div>
              </div>
              {errors.npfReturnRate && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors.npfReturnRate}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Средняя доходность НПФ в России 5-8% годовых
              </p>
            </div>
          )}
          
          {formData.voluntaryPensionType === 'individual' && (
            <div className="mb-3">
              <label htmlFor="selfInvestmentType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Инвестиционная стратегия
              </label>
              <select
                id="selfInvestmentType"
                name="selfInvestmentType"
                value={formData.selfInvestmentType}
                onChange={handleInputChange}
                className={`form-select w-full ${errors.selfInvestmentType ? 'input-error' : ''}`}
              >
                {investmentStrategies.map((strategy) => (
                  <option key={strategy.id} value={strategy.id}>
                    {strategy.name} ({strategy.returnRate}%, риск: {strategy.risk})
                  </option>
                ))}
              </select>
              {errors.selfInvestmentType && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors.selfInvestmentType}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      <div className="mt-4 bg-blue-100 dark:bg-blue-900/30 p-3 rounded-lg">
        <div className="flex items-start">
          <TrendingUp className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3 mt-1" />
          <div>
            <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">
              Влияние финансовых параметров
            </h4>
            <p className="text-xs text-blue-600 dark:text-blue-400">
              Официальная высокая зарплата увеличивает страховую пенсию. Регулярные накопления с разумной 
              доходностью формируют существенную накопительную часть. Важно учитывать влияние инфляции 
              на покупательную способность будущей пенсии.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialDataSection;